import React, { useContext } from 'react'
import { NotificationManagerContext, Spinner, View } from 'oio-react'
import tinycolor from 'tinycolor2'
import { CheckmarkCircledIcon } from 'assets/icons'
import initiativePrivacyConstants from 'config/constants/initiativePrivacy'
import { useMe, useOrganization, useUpdateInitiative } from 'src/core/graphql/hooks'
import { InitiativeHierarchyContext } from 'src/sites/kits/Utils/InitiativeHierarchy'

const InitiativeSettingsPrivacyBlock = () => {
   const { me } = useMe()
   const { organization } = useOrganization()
   const { initiative } = useContext(InitiativeHierarchyContext)
   const adminRole = initiative.roles.find(r => r.type === 'admin')
   const primaryRole = initiative.roles.find(r => r.type === 'primary')

   const privacyOptions = initiativePrivacyConstants
   const currentPrivacyConstants = privacyOptions.find(p => p.value === initiative.privacy)

   // Conditionally show draft option
   // Note: for now, we conditionally disable draft option, but we may also in the future want to
   // conditionally disable admin-only option as well if initiative already has primary role users
   // PR #878
   const addedByMe = initiative.addedBy.id === me.id
   const isDraft = initiative.privacy === 'draft'
   const hasOtherParticipants = adminRole?.numActiveUsers > 1 || primaryRole?.numActiveUsers > 0
   const showDraftOption = isDraft || (addedByMe && !hasOtherParticipants)

   const availablePrivacyOptions = showDraftOption
      ? privacyOptions
      : privacyOptions.filter(option => option.value !== 'draft')

   const { textColor } = currentPrivacyConstants
   const { updateInitiative, mutating } = useUpdateInitiative()
   const { showNotification } = useContext(NotificationManagerContext)

   const handleSelectPrivacyOption = async (privacy) => {
      try {
         await updateInitiative({ id: initiative.id }, { privacy })
         showNotification({
            message: 'Changes saved successfully',
            title: 'Success!',
            type: 'success'
         })
      } catch (err) {
         showNotification({
            message: err.message || 'An error occurred while saving changes',
            title: 'Error',
            type: 'error'
         })
      }
   }

   return (
      <div
         className="relative flex w-full flex-col gap-0 rounded-xl px-6 pb-2 mb-8"
         style={{
            backgroundColor: currentPrivacyConstants.backgroundColor
         }}>
         <View flex="1 1 auto">
            <div className="text-sm py-5" style={{ color: textColor }}>
               <b>{initiative.name}</b>
               {' is currently set to: '}
               <b>{currentPrivacyConstants.name}</b>
            </div>
            {availablePrivacyOptions.map((option, index) => (
               <View
                  key={option.name}
                  wdith="100%"
                  display="flex"
                  padding="12px 0px"
                  borderTop={`1px solid ${tinycolor(textColor).setAlpha(0.1).toRgbString()}`}>
                  <View
                     onClick={() => handleSelectPrivacyOption(option.value)}
                     flex="0 0 auto"
                     marginRight="20px"
                     paddingTop="5px"
                     style={{ cursor: 'pointer' }}>
                     {initiative.privacy === option.value && (
                        <CheckmarkCircledIcon
                           width="20px"
                           height="20px"
                           color={['admin', 'draft'].includes(option.value) ? '#ffc958' : '#2b7fff'}
                        />
                     )}
                     {initiative.privacy !== option.value && (
                        <View
                           width="20px"
                           height="20px"
                           border={`2px solid ${tinycolor(textColor).setAlpha(0.3).toRgbString()}`}
                           borderRadius="50%"
                        />
                     )}
                  </View>
                  <View flex="1 1 auto">
                     <div className="text-sm font-semibold">{option.name}</div>
                     <div className="text-sm opacity-70">
                        {option.settingsDescription(initiative, organization)}
                     </div>
                  </View>
               </View>
            ))}
         </View>
         {mutating && (
            <View
               display="flex"
               justifyContent="center"
               alignItems="center"
               position="absolute"
               top="0px"
               left="0px"
               right="0px"
               bottom="0px"
               backgroundColor="rgba(255,255,255,0.95)"
               zIndex="2">
               <Spinner width="40px" height="40px" color="#aaa" />
            </View>
         )}
      </div>
   )
}

export default InitiativeSettingsPrivacyBlock
